import type {
  LinkedAccount,
  Membership,
} from '@collinsonx/utils/generatedTypes/graphql';

import { decodeJWT } from '@collinsonx/jwt';
import { isPayloadValid, parsePayload, removeItem } from '@lib';
import {
  LOGOUT_REASON,
  OriginType,
  sendErrorLog,
  sendInfoLog,
  serializeError,
  useLogStorage,
} from '@lib/logging';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_PLATFORM } from 'config/constants';
import { BookingError, BookingInfo } from 'config/datadog';
import {
  authenticateToContinueSession,
  getParamsFromQueryAndStorage,
  isRoute,
  ROUTES,
} from 'config/navigation';
import { DEFAULT_WEB_REFERRER } from 'config/productDefaultConfig';
import { BOOKING_ID } from 'config/storage';
import useLocale from 'hooks/useLocale';
import { useRouter } from 'next/router';
import {
  createContext,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BridgePayload } from 'types/booking';
import { Consumer } from 'types/consumer';

import useError from './useError';

type PayloadState = {
  accountProvider?: string;
  bookingId?: string;
  consumerData: Consumer | undefined;
  consumerID?: string;
  isJwtValid?: boolean;
  isLocationProduct: boolean;
  jwt: string | undefined;
  linkedAccountId: string | undefined;
  locale: string;
  loungeCode: string | undefined;
  matchedAccount: LinkedAccount | undefined;
  membership?: Membership;
  membershipID?: string;
  membershipType?: string;
  payload: BridgePayload | undefined;
  platform: string | undefined;
  referrerUrl: string | undefined;
  setConsumerData: (consumer: Consumer) => void;
  setMatchedAccount: (matchedAccount: LinkedAccount) => void;
  setMatchedMembership: (membership: Membership) => void;
  setPayload: (payload: BridgePayload) => void;
  sourceCode?: string;
};

export const PayloadContext = createContext<null | PayloadState>(null);

export const PayloadProvider = (props: PropsWithChildren) => {
  const { setError } = useError();
  const router = useRouter();

  const [payload, setPayload] = useState<BridgePayload>();
  const [loungeCode, setLoungeCode] = useState<string>();
  const [bookingId, setBookingId] = useState<string>();
  const [jwt, setJWT] = useState<string>();
  const [matchedAccount, setMatchedAccount] = useState<LinkedAccount>();
  const [matchedMembership, setMatchedMembership] = useState<Membership>();
  const [referrerUrl, setReferrerUrl] = useState<string>();
  const [platform, setPlatform] = useState<string>(DEFAULT_PLATFORM);
  const [consumerData, setConsumerData] = useState<Consumer>();
  const [language, setLanguage] = useState<string>('');
  const [version, setVersion] = useState<string>();
  // const [isJwtValid, setJwtIsValid] = useState<boolean>(false);

  const translations = useLocale();
  const { setPayloadData } = useLogStorage();
  const payloadValidatedRef = useRef(false);

  // const secret = process.env.NEXT_PUBLIC_JWT_SECRET || '';

  const stateData = {
    bookingId: bookingId,
    language: language,
    loungeCode: loungeCode,
    platform: platform,
    referrer: referrerUrl,
    token: jwt,
    version: version,
    consumerData,
    linkedAccountId: matchedAccount?.id,
    payload,
  };

  useEffect(() => {
    const hydratePayload = async () => {
      if (isRoute(ROUTES.HOME)) removeItem(BOOKING_ID);
      const params = getParamsFromQueryAndStorage(stateData);

      if (!params.loungeParam || !params.tokenParam) {
        sendErrorLog(BookingError.UNABLE_TO_RETRIEVE_PARAMS, {
          origin: OriginType.Platform,
          params: stateData,
        });

        setError({ message: translations.generic.error.tokenNoFound });
        return;
      }

      const isWebPlatform = params.platformParam === 'web';
      const referrer =
        params.referrerParam || (isWebPlatform ? DEFAULT_WEB_REFERRER : '');
      if (referrer) setReferrerUrl(referrer);
      if (params.bookingIdParam) setBookingId(params.bookingIdParam);

      setPlatform(params.platformParam);
      setLoungeCode(params.loungeParam);
      setJWT(params.tokenParam);
      setLanguage(params.languageParam);
      setVersion(params.versionParam);

      let payloadParam: BridgePayload;

      try {
        payloadParam = decodeJWT(params.tokenParam) as BridgePayload;
        payloadParam = parsePayload(payloadParam);
      } catch (e) {
        sendErrorLog(BookingError.JWT_DECODE_ERROR, {
          errorMessage: serializeError(e),
          origin: OriginType.Platform,
          params: stateData,
        });
        setError({ message: translations.generic.error.tokenNoFound });
        return;
      }

      if (!isPayloadValid(payloadParam)) {
        if (!payloadValidatedRef.current) {
          payloadValidatedRef.current = true;
          sendInfoLog(BookingInfo.INVALID_PAYLOAD_RECEIVED, {
            accountProvider: payloadParam.accountProvider,
            externalId: payloadParam.externalId,
            membershipNumber: payloadParam.membershipNumber,
            sourceCode: payloadParam.sourceCode,
          });
        }
        sendErrorLog(BookingError.JWT_VALIDATION_ERROR, {
          origin: OriginType.Platform,
          params: stateData,
          payloadParam,
        });
        setError({ message: translations.generic.error.tokenNoFound });
        return;
      }

      if (!payloadValidatedRef.current) {
        payloadValidatedRef.current = true;
        sendInfoLog(BookingInfo.VALID_PAYLOAD_RECEIVED, {
          accountProvider: payloadParam.accountProvider,
          externalID: payloadParam.externalId,
          membershipNumber: payloadParam.membershipNumber,
          sourceCode: payloadParam.sourceCode,
        });
      }

      setPayload(payloadParam);

      // TODO: Investigate JWT verification as the expiration time is incorrect
      // and is constantly failing from email links
      // try {
      //   const { error, isValid } = await verifyJWT(jwt, secret);
      //   setJwtIsValid(isValid);

      //   if (!isValid || error) {
      //     console.log('isValid, error', isValid, error);
      //     sendErrorLog(BookingError.JWT_VALIDATION_ERROR, {
      //       ...stateData,
      //       error,
      //       validationError: error?.message,
      //     });

      //     if (isPageProtected()) authenticateToContinueSession();
      //   }
      // } catch (err) {
      //   console.log('err', err);
      //   sendErrorLog(BookingError.JWT_VALIDATION_ERROR, {
      //     ...stateData,
      //     error: err,
      //   });

      //   setError({ message: translations.lounge.errors.server.title});
      // }

      if (!params.cachedJwtString || !params.tokenParam) return;

      const cachedPayload = params.cachedJwtString
        ? decodeJWT(params.cachedJwtString)
        : {};

      const cachedPayloadParsed = parsePayload(cachedPayload as BridgePayload);

      const queryPayload = decodeJWT(params.tokenParam);
      const queryPayloadParsed = parsePayload(queryPayload as BridgePayload);

      if (
        queryPayloadParsed.email !== cachedPayloadParsed.email ||
        queryPayloadParsed.membershipNumber !==
          cachedPayloadParsed.membershipNumber ||
        queryPayloadParsed.sourceCode !== cachedPayloadParsed.sourceCode
      ) {
        await authenticateToContinueSession(
          cachedPayload.token
            ? {
                cachedPayload: {
                  externalID: cachedPayloadParsed.externalId,
                  membershipNumber: cachedPayloadParsed.membershipNumber,
                  sourceCode: cachedPayloadParsed.sourceCode,
                },
                consumerData: consumerData?.getConsumerByID,
                consumerID: consumerData?.getConsumerByID.id,
                payload: {
                  bookingID: params.bookingIdParam ?? '',
                  externalID: queryPayloadParsed.externalId,
                  membershipNumber: queryPayloadParsed.membershipNumber,
                  sourceCode: queryPayloadParsed.sourceCode,
                },
                reason: LOGOUT_REASON.AUTHENTICATED_USER.PAYLOAD_MISMATCH,
              }
            : undefined
        );
      }
    };

    hydratePayload();
  }, [router.pathname]);

  useEffect(() => {
    setPayloadData({
      accountProvider: payload?.accountProvider,
      externalID: payload?.externalId,
      language,
      loungeCode,
      membershipNumber: payload?.membershipNumber,
      membershipType: payload?.membershipType,
      platform,
      referrer: referrerUrl,
      sourceCode: payload?.sourceCode,
      version,
    });
  }, [payload, language, loungeCode, platform, referrerUrl, version]);

  return (
    <PayloadContext.Provider
      value={{
        bookingId,
        consumerData,
        consumerID: consumerData?.getConsumerByID.id,
        isLocationProduct: Boolean(loungeCode?.endsWith('FT')),
        // isJwtValid,
        jwt,
        linkedAccountId: matchedAccount?.id,
        locale: language,
        loungeCode,
        matchedAccount,
        membership: matchedMembership,
        membershipID: matchedMembership?.id,
        membershipType: payload?.membershipType,
        payload,
        platform,
        referrerUrl,
        setConsumerData,
        setMatchedAccount,
        setMatchedMembership,
        setPayload,
        sourceCode:
          matchedMembership?.membershipNumber?.split('_')?.[0] ||
          matchedAccount?.sourceCode ||
          '',
      }}
    >
      {props.children}
    </PayloadContext.Provider>
  );
};
